import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    isLoggedIn: false,
    user: '',
    memberType: '',
    adminId: '',
    userId: ''
  });

  // Check auth status on mount and when sessionStorage changes
  const checkAuthStatus = () => {
    const isLoggedIn = sessionStorage.getItem('isLoggedIn');
    const user = sessionStorage.getItem('user') || '';
    const memberType = sessionStorage.getItem('member_type') || '';
    const adminId = sessionStorage.getItem('admin_id') || '';
    const userId = sessionStorage.getItem('user_id') || '';

    return {
      isLoggedIn: !!isLoggedIn,
      user,
      memberType,
      adminId,
      userId
    };
  };

  // Initialize on component mount
  useEffect(() => {
    const authStatus = checkAuthStatus();
    setAuthState(authStatus);
  }, []);

  // Listen for custom event when login happens
  useEffect(() => {
    const handleAuthChange = () => {
      const authStatus = checkAuthStatus();
      setAuthState(authStatus);
    };

    // Listen for custom event (triggered after login)
    window.addEventListener('auth-state-changed', handleAuthChange);
    
    // Also check periodically (every 500ms) - simple solution
    const interval = setInterval(() => {
      const authStatus = checkAuthStatus();
      if (authStatus.isLoggedIn !== authState.isLoggedIn) {
        setAuthState(authStatus);
      }
    }, 500);

    return () => {
      window.removeEventListener('auth-state-changed', handleAuthChange);
      clearInterval(interval);
    };
  }, [authState.isLoggedIn]);

  const login = (userData) => {
    // Store in sessionStorage
    sessionStorage.setItem('isLoggedIn', 'true');
    if (userData.user) sessionStorage.setItem('user', userData.user);
    if (userData.member_type) sessionStorage.setItem('member_type', userData.member_type);
    if (userData.admin_id) sessionStorage.setItem('admin_id', userData.admin_id);
    if (userData.user_id) sessionStorage.setItem('user_id', userData.user_id);

    // Trigger custom event
    window.dispatchEvent(new Event('auth-state-changed'));
  };

  const logout = () => {
    // Clear sessionStorage
    sessionStorage.removeItem('isLoggedIn');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('member_type');
    sessionStorage.removeItem('admin_id');
    sessionStorage.removeItem('user_id');

    // Trigger custom event
    window.dispatchEvent(new Event('auth-state-changed'));
  };

  return (
    <AuthContext.Provider value={{ ...authState, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};